import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, type Control } from 'react-hook-form'
import {
  IIbanStatus,
  type IAccountInfoProfileBankAccount,
} from '~/@api/generated-api-types-user/Api'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shadcn/ui/select'
import { FieldContent, FieldError, FieldLabel } from '~/shadcn/ui/field'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { CurrencyInput } from '~/@mid_ui-form/currency-input/CurrencyInput'
import type { WalletSchema } from '~/@pages_investing/projects/_project_types/wallet.types'
import { useUserNotifications } from '~/@biz_notifications/user-notifications/useUserNotifications'
import { WithdrawFundsInfo } from '../withdraw-funds-info/WithdrawFundsInfo'
import { useWithdrawFunds } from './useWithdrawFunds'
import { type FormModel, formModelSchema } from './schema'
import { WithdrawFundsConfirmDialog } from './WithdrawFundsConfirmDialog'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { Button } from '~/shadcn/ui/button/button'

type Props = {
  wallet: WalletSchema
  bankAccounts: IAccountInfoProfileBankAccount[]
}

export function WithdrawFundsForm({ wallet, bankAccounts }: Props) {
  const navigate = useNavigate()
  const { setNotification } = useLocalNotifications()
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)

  const { reloadNotifications } = useUserNotifications()
  const submittedFormDataRef = useRef<FormModel | null>(null)

  const {
    withdrawFunds: withdrawFundsMutation,
    isRequesting: isWithdrawFundsPending,
    error,
  } = useWithdrawFunds()

  const closeConfirmDialog = () => setIsConfirmDialogOpen(false)
  const openConfirmDialog = () => setIsConfirmDialogOpen(true)

  const withdrawToIbanOptions = bankAccounts
    .filter((bankAccount) => bankAccount.statusId === IIbanStatus.Verified)
    .map(({ iban }) => ({ label: iban, value: iban }))

  const form = useForm({
    resolver: zodResolver(formModelSchema),
    defaultValues: {
      toIban: '',
      amount: Math.min(50, wallet.funds?.available ?? 0),
      disponibleFunds: wallet.funds?.available ?? 0,
      isMyAccount: false,
    },
  })

  const onSubmit = (formModel: FormModel) => {
    submittedFormDataRef.current = formModel
    openConfirmDialog()
  }

  const onWithdrawFundsConfirm = async () => {
    if (!submittedFormDataRef.current) {
      console.error('submittedFormDataRef.current is null')
      setNotification('error', 'Vyskytla sa neočakávaná chyba')
      return
    }

    const { amount, toIban } = submittedFormDataRef.current

    withdrawFundsMutation(
      {
        walletId: wallet._id,
        iban: toIban,
        amount: Number(amount),
      },
      {
        onSuccess: () => {
          Analytics.track('withdraw-funds')
          closeConfirmDialog()
          reloadNotifications()
          navigate('/account')
        },
        onError: () => {
          closeConfirmDialog()
        },
      }
    )
  }

  return (
    <>
      <Form {...form}>
        <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-10 items-baseline">
            <FormField
              name="toIban"
              rules={{ required: true }}
              control={form.control}
              render={({ fieldState, field }) => (
                <FormItem className="w-full">
                  <FieldLabel>Na aký účet</FieldLabel>
                  <FieldContent>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={
                        isWithdrawFundsPending || !withdrawToIbanOptions
                      }>
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={!!fieldState.error}>
                        <SelectValue placeholder="Vyberte účet" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectGroup>
                          {withdrawToIbanOptions?.map(
                            (option: { label: string; value: string }) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}>
                                {option.label}
                              </SelectItem>
                            )
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {fieldState.error && (
                      <FieldError className="text-sm text-destructive">
                        {fieldState.error.message}
                      </FieldError>
                    )}
                  </FieldContent>
                </FormItem>
              )}
            />

            <FormField
              name="amount"
              control={form.control}
              render={({ fieldState, field }) => (
                <FormItem className="w-full font-semibold">
                  <CurrencyInput
                    disabled={isWithdrawFundsPending}
                    labelText="Čiastka ktorú chcete vybrať"
                    value={Number(field.value)}
                    onChangeValue={field.onChange}
                  />
                  {fieldState.error && (
                    <FieldError className="text-sm text-destructive">
                      {fieldState.error.message}
                    </FieldError>
                  )}
                </FormItem>
              )}
            />

            <WithdrawFundsInfo control={form.control as Control<FormModel>} />

            <FormField
              control={form.control}
              name="isMyAccount"
              render={({ fieldState, field }) => (
                <FormItem className="w-full">
                  <FieldContent>
                    <FieldLabel className="flex flex-row items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isWithdrawFundsPending}
                      />
                      <span className="text-base font-medium leading-normal">
                        Uvedený bankový účet je môj
                      </span>
                    </FieldLabel>
                  </FieldContent>
                  {fieldState.error && (
                    <FieldError className="text-sm text-destructive">
                      {fieldState.error.message}
                    </FieldError>
                  )}
                </FormItem>
              )}
            />
            <Button
              variant="accent"
              size="adaptive"
              disabled={
                !!Object.keys(form.formState.errors).length ||
                isWithdrawFundsPending
              }
              type="submit">
              <span>Potvrdiť výber</span>
              <ArrowForwardIcon className="ml-2" />
            </Button>

            {error && <FormError error={error} />}
          </div>
        </form>
      </Form>
      <WithdrawFundsConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={onWithdrawFundsConfirm}
        isRequesting={isWithdrawFundsPending}
      />
    </>
  )
}
